import { useEffect, useMemo, useRef, useState } from "react"
import { callautopopulate } from "../../../services/callautopop";
import _ from "underscore";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { dateConvert, getFullMonthSelected } from "../../../utils/Helper";
import { useLocation } from "react-router-dom";
import { DateObject } from "react-multi-date-picker";

export const useEmployeeAttendanceInfo = () => {

    const tagRef = useRef(null);
    const [options, setOptions] = useState({ users: [], leads: [], projects: [] });
    const [userOptions, setUserOptions] = useState([]);
    const [hoveredRange, setHoveredRange] = useState([]);
    const [filterValues, setFilterValues] = useState({ users: [], leads: [], projects: [] });
    const [columnDefs, setColumnDefs] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [isChange, setIsChange] = useState(false);
    const location = useLocation()?.state;
    const [legendItems, setLegendItems] = useState([]);
    const [selectedTab, setSelectedTab] = useState("attendance");
    const [callPunchApi, setCallPunchApi] = useState(false);
    const [labelCols, setLabelCols] = useState([]);

    useEffect(() => {
        callautopopulate("users", "project_leads", "user_info").then((res) => {
            setUserOptions(_.sortBy(res?.users, "user_display_name"));
            setOptions((prev) => ({
                ...prev, leads: _.sortBy(res?.project_leads, "user_display_name"),
            }));

            // set selected lead by default
            setFilterValues((prev) => ({ ...prev, leads: [_.findWhere(res?.project_leads, { user_code: location?.selectedEmployee?.user_code || res?.user_info[0]?.user_code })], view_group_wise: res?.user_info?.[0]?.is_user_grouping_view || false }));
        })
        // select current month as default
        const dates = getFullMonthSelected();
        const start = new DateObject(dates?.from_date)
        const end = new DateObject(dates?.to_date)
        setHoveredRange([start, end]);

    }, [])

    useEffect(() => {
        setSelectedTab(location?.key || "attendance")
    }, [location?.key])


    useEffect(() => {
        handleSetUserOptions();
    }, [userOptions])

    useEffect(() => {
        (filterValues.apiCall && hoveredRange?.[0] && hoveredRange?.[1]) && ((selectedTab === "attendance" && filterValues?.view_group_wise !== undefined) ? getAttendanceData() : setCallPunchApi(true));
        selectedTab !== "attendance" && setRowData([]);
    }, [hoveredRange, selectedTab, filterValues?.view_group_wise, filterValues?.apiCall])


    useEffect(() => {
        getColumnDefs()
    }, [hoveredRange, rowData])

    const getColumnDefs = () => {
        if (hoveredRange && hoveredRange[0] !== undefined && hoveredRange[1] !== undefined) {
            setColumnDefs([
                {
                    field: "group_name",
                    headerName: "Manager Name",
                    rowGroup: true,
                    hide: true,
                    width: 150,
                },
                {
                    field: "user_display_name",
                    headerName: "Employee Name",
                    width: 150,
                    pinned: "left"
                }
            ]);
            const start = hoveredRange[0];
            const end = hoveredRange[1];
            const columnNames = [];
            const currentDate = new Date(start);
            const endDate = new Date(end);

            while (currentDate <= endDate) {
                const formattedDate = currentDate.toLocaleDateString("en-GB").split("/").reverse().join("-");
                columnNames.push(formattedDate);
                currentDate.setDate(currentDate.getDate() + 1);
            }
            [...columnNames, ..._.filter(labelCols, item => !item?.hide)].forEach((col) => {
                if (col?.type_name) {
                    const colObj = {
                        field: col?.type_name,
                        width: 70,
                        headerClass: "header-wrap",
                        headerComponentParams: {
                            template: `<div style="text-transform: capitalize;" class="text-center w100">${col?.type_name?.split("_")?.join(" ")}</div>`
                        },
                        cellClass: "text-center"
                    }
                    setColumnDefs((prev) => [...prev, colObj]);
                } else {
                    const dateObj = new Date(col);

                    const dayHeader = dateObj.toLocaleDateString("en-US", { day: "numeric", });
                    const monthHeader = dateObj.toLocaleDateString("en-US", { month: "short", });
                    const weekDay = dateObj.toString().slice(0, 3);

                    const formattedMonth = monthHeader.length > 3 ? monthHeader.slice(0, 3) : monthHeader;

                    let columnObject = {
                        headerName: `${formattedMonth}\n${dayHeader}\n${weekDay}`,
                        field: col,
                        width: 50,
                        headerClass: `header-wrap ${weekDay === "Sun" ? "sunday" : ""}`,
                        suppressHeaderMenuButton: true,
                        cellRenderer: function (params) {
                            return (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "100%" }}>
                                    <div className="position-relative" style={{ width: "14px", height: "14px", borderRadius: "50%", background: `conic-gradient(${params?.value?.first_half_color} 0deg 180deg, ${params.value?.second_half_color} 180deg 360deg)` }} >
                                        <span
                                            title={params?.value?.second_half} className="position-absolute top-0 start-0 w50 employee-dash-height"
                                        />
                                        <span
                                            title={params?.value?.first_half} className="position-absolute top-0 start-50 w50 employee-dash-height"
                                        />
                                    </div>
                                </div>
                            )
                        },
                    };
                    setColumnDefs((prev) => [...prev, columnObject]);
                }
            });
        }
    };

    const getAttendanceData = async (emptyLead, emptyUser, userSelected, emptyProject, refreshKey) => {
        try {
            setLoader(true);
            setRowData([]);
            // get group wise flag from autopopulate when refresh
            let group = filterValues?.view_group_wise;
            if (refreshKey === "refresh") {
                await callautopopulate("user_info")?.then(res => {
                    group = res?.user_info?.[0]?.is_user_grouping_view;
                    setFilterValues((prev) => ({ ...prev, view_group_wise: res?.user_info?.[0]?.is_user_grouping_view || false }));
                })
            }

            if (group !== filterValues?.view_group_wise) {
                return;
            }
            const payload = {
                from_date: dateConvert(hoveredRange?.[0]?.toDate(), 'iso-string') || "",
                to_date: dateConvert(hoveredRange?.[1]?.toDate(), 'iso-string') || "",
                project_lead_user_code: emptyLead ? "" : _.pluck(filterValues?.project_leads, "user_code")?.toString() || "",
                users: emptyUser ? "" : userSelected ? _.pluck(userSelected, "user_code")?.toString() : _.pluck(filterValues?.users, "user_code")?.toString(),
                attendance_type: _.pluck(filterValues?.attendance_type, "attendance_type_key")?.toString(),
                project_ids: emptyProject ? "" : _.pluck(filterValues?.projects, "project_id")?.toString() || ""
            }
            const res = await services.getInvokeRemote("POST", Urls("GETEMPLOYEEATTENDANCEREPORT"), payload);
            if (res) {
                const data = res?.body?.data?.manager_employee_attendance_view;
                let dynamicColsNames = res?.body?.data?.attendance_type_details;
                dynamicColsNames?.map(item => {
                    item["hide"] = data?.[0]?.[item?.type_name] === undefined;
                })
                setLabelCols(dynamicColsNames);
                // set legends
                const allColors = _.reduce(data, (result, item) => {
                    return result.concat([
                        { color: item.first_half_color, title: item.first_half },
                        { color: item.second_half_color, title: item.second_half }
                    ]);
                }, []);
                let uniqColors = _.uniq(allColors, false, (item) => {
                    return item.color + '-' + item.title;
                });
                uniqColors = _.sortBy(_.filter(uniqColors, item => item?.color), "title")
                setLegendItems(uniqColors);

                // set row data
                const allUsers = _.uniq(_.pluck(data, "user_code"));
                let arr = [];
                allUsers?.forEach((user) => {
                    const findData = _.filter(data, { user_code: user });
                    let obj = {
                        user_display_name: findData?.[0]?.user_display_name,
                        user_code: user,
                        group_name: (group ? findData?.[0]?.group_name : findData?.[0]?.manager_name) || findData?.[0]?.manager_name
                    }
                    findData?.forEach((item) => {
                        dynamicColsNames?.forEach(label => {
                            if (item?.[label?.type_name]) {
                                obj[label?.type_name] = _.filter(_.pluck(findData, label?.type_name), item => item).reduce((sum, num) => sum + num, 0);
                            }
                        })
                        obj[item?.day_date?.split("T")?.[0]] = item
                    })
                    arr.push(obj);
                })
                const sortedArray = arr.sort((a, b) => { return a?.group_name?.localeCompare(b.group_name) || a?.user_display_name?.localeCompare(b?.user_display_name) });
                setRowData(_.filter(sortedArray, (item) => item?.user_display_name));
                setLoader(false);
                setFilterValues((prev) => ({ ...prev, apiCall: false }))
            }
        } catch (error) {
            console.error("Error: ", error);
            setLoader(false);
        }
    }

    const handleSetUserOptions = () => {
        let userSelected = [];
        if (!_.isEmpty(filterValues?.leads)) {
            let data = []
            filterValues?.leads?.forEach((item) => {
                const users = _.filter(userOptions, (user) => user?.reporting_employee_id === item?.employee_id);
                data = data.concat(users);
            })
            const sortedData = data?.sort((a, b) => a?.user_display_name?.localeCompare(b?.user_display_name, undefined, { sensitivity: 'base' }));
            setOptions((prev) => ({ ...prev, users: sortedData }));
            filterValues?.users?.forEach((item) => {
                if (!_.isEmpty(_.findWhere(sortedData, { user_code: item?.user_code }))) {
                    userSelected.push(item);
                }
            })
            setFilterValues((prev) => ({ ...prev, users: userSelected }));
        } else {
            const sortedData = userOptions?.sort((a, b) => a.user_display_name.localeCompare(b.user_display_name, undefined, { sensitivity: 'base' }));
            setOptions((prev) => ({ ...prev, users: sortedData }));
        }
        if (isChange) {
            selectedTab === "attendance" ? getAttendanceData(false, _.isEmpty(userSelected) ? true : false, userSelected) : setCallPunchApi(true);
            setIsChange(false);
        }
    }

    return useMemo(() => ({
        options, filterValues, setFilterValues, hoveredRange, setHoveredRange, columnDefs, rowData, getAttendanceData, loader, isChange, setIsChange, legendItems, handleSetUserOptions, tagRef, selectedTab, setSelectedTab, callPunchApi, setCallPunchApi
    }), [options, filterValues, setFilterValues, hoveredRange, setHoveredRange, columnDefs, rowData, getAttendanceData, loader, isChange, setIsChange, legendItems, handleSetUserOptions, tagRef, selectedTab, setSelectedTab, callPunchApi, setCallPunchApi])
}